<div class="modify-page">
  @if (loading) {
    <div class="state-card">
      <div class="spinner"></div>

      <h2>Loading appointment</h2>

      <p>We're getting your booking ready to edit.</p>
    </div>
  } @else if (errorMessage && !appointment) {
    <div class="state-card state-card--error">
      <div class="state-icon">!</div>

      <h2>Unable to load appointment</h2>

      <p>{{ errorMessage }}</p>

      <button type="button" class="btn btn--dark" (click)="cancel()">
        Back to appointment
      </button>
    </div>
  } @else {
    <section class="modify-hero">
      <div class="modify-hero__copy">
        <span class="eyebrow"> Manage booking </span>

        <h1>Modify Appointment</h1>

        <p>Update your services, specialist, date or appointment time.</p>
      </div>

      <button type="button" class="btn btn--light" (click)="cancel()">
        Back to appointment
      </button>
    </section>

    @if (errorMessage) {
      <div class="alert alert--error">
        <div>
          <strong> We couldn't save your changes </strong>

          <span>
            {{ errorMessage }}
          </span>
        </div>

        <button
          type="button"
          (click)="errorMessage = ''"
          aria-label="Dismiss error"
        >
          ×
        </button>
      </div>
    }

    <div class="modify-layout">
      <main class="editor-column">
        <section class="section-card">
          <div class="section-heading">
            <div>
              <span class="section-kicker"> Step 1 </span>

              <h2>Services</h2>

              <p>Select the services you want included in this appointment.</p>
            </div>

            <span class="selection-count">
              {{ selectedServiceIds.length }}
              selected
            </span>
          </div>

          <div class="service-grid">
            @for (service of services; track service.service_id) {
              <button
                type="button"
                class="service-card"
                [class.service-card--selected]="
                  isServiceSelected(service.service_id)
                "
                (click)="toggleService(service.service_id)"
              >
                <span class="service-check">
                  @if (isServiceSelected(service.service_id)) {
                    ✓
                  }
                </span>

                <span class="service-card__copy">
                  <strong>
                    {{ service.name }}
                  </strong>

                  <span>
                    {{ service.duration_minutes }}
                    min
                  </span>
                </span>

                <strong class="service-price">
                  {{ formatMoney(service.price) }}
                </strong>
              </button>
            }
          </div>
        </section>

        <section class="section-card">
          <div class="section-heading">
            <div>
              <span class="section-kicker"> Step 2 </span>

              <h2>Specialist</h2>

              <p>Choose who you'd like to see.</p>
            </div>
          </div>

          <div class="staff-grid">
            @for (staff of staffMembers; track staff.staff_id) {
              <button
                type="button"
                class="staff-card"
                [class.staff-card--selected]="
                  selectedStaffId === staff.staff_id
                "
                (click)="selectedStaffId = staff.staff_id; onStaffChange()"
              >
                <span class="staff-avatar">
                  {{ staff.full_name?.charAt(0)?.toUpperCase() }}
                </span>

                <span class="staff-copy">
                  <strong>
                    {{ staff.full_name }}
                  </strong>

                  <span>
                    {{ staff.job_title || "Specialist" }}
                  </span>
                </span>

                <span class="staff-check">
                  @if (selectedStaffId === staff.staff_id) {
                    ✓
                  }
                </span>
              </button>
            }
          </div>
        </section>

        <section class="section-card">
          <div class="section-heading">
            <div>
              <span class="section-kicker"> Step 3 </span>

              <h2>Date & Time</h2>

              <p>Choose a new appointment slot.</p>
            </div>
          </div>

          <div class="date-field">
            <label for="appointment-date"> Appointment date </label>

            <input
              id="appointment-date"
              type="date"
              [(ngModel)]="selectedDate"
              (ngModelChange)="onDateChange()"
            />
          </div>

          @if (!selectedStaffId) {
            <div class="availability-note">
              Choose a specialist to view available times.
            </div>
          } @else if (!selectedServiceIds.length) {
            <div class="availability-note">
              Select at least one service to view available times.
            </div>
          } @else if (loadingAvailability) {
            <div class="availability-note">Loading available times...</div>
          } @else {
            @if (availabilityMessage) {
              <div class="availability-note">
                {{ availabilityMessage }}
              </div>
            }

            <div class="time-grid">
              @for (slot of availableSlots; track slot.time) {
                <button
                  type="button"
                  class="time-slot"
                  [disabled]="!slot.available"
                  [class.time-slot--selected]="selectedTime === slot.time"
                  (click)="selectTime(slot)"
                >
                  {{ slot.time }}
                </button>
              }
            </div>
          }
        </section>

        <section class="section-card">
          <div class="section-heading">
            <div>
              <span class="section-kicker"> Optional </span>

              <h2>Notes</h2>

              <p>Add anything you'd like the salon to know.</p>
            </div>
          </div>

          <textarea
            class="notes-field"
            rows="5"
            maxlength="1000"
            [(ngModel)]="notes"
            placeholder="Add a note for the salon..."
          ></textarea>
        </section>
      </main>

      <aside class="summary-column">
        <section class="summary-card">
          <span class="section-kicker"> Updated booking </span>

          <h2>
            {{ appointment?.salon?.name || "Appointment" }}
          </h2>

          <p class="salon-address">
            {{ appointment?.salon?.address }}

            @if (appointment?.salon?.city) {
              <span>
                ,
                {{ appointment.salon.city }}
              </span>
            }
          </p>

          <div class="summary-divider"></div>

          <div class="summary-date">
            <div class="summary-date__calendar">
              <span>
                {{ selectedDate ? (selectedDate | date: "MMM") : "—" }}
              </span>

              <strong>
                {{ selectedDate ? (selectedDate | date: "dd") : "—" }}
              </strong>
            </div>

            <div>
              <strong>
                {{
                  selectedDate ? (selectedDate | date: "EEEE") : "Choose a date"
                }}
              </strong>

              <span>
                {{ selectedTime || "Choose a time" }}
              </span>
            </div>
          </div>

          <div class="summary-divider"></div>

          <div class="summary-section">
            <span class="summary-label"> Services </span>

            @if (selectedServices.length) {
              @for (service of selectedServices; track service.service_id) {
                <div class="summary-service">
                  <div>
                    <strong>
                      {{ service.name }}
                    </strong>

                    <span>
                      {{ service.duration_minutes }}
                      min
                    </span>
                  </div>

                  <strong>
                    {{ formatMoney(service.price) }}
                  </strong>
                </div>
              }
            } @else {
              <p class="summary-empty">No services selected.</p>
            }
          </div>

          <div class="summary-divider"></div>

          <div class="summary-row">
            <span>Duration</span>

            <strong> {{ totalDuration }} min </strong>
          </div>

          <div class="summary-row summary-row--total">
            <span>Estimated total</span>

            <strong>
              {{ formatMoney(estimatedTotal) }}
            </strong>
          </div>

          <p class="summary-footnote">
            Your final price is calculated securely by the salon when the
            appointment is updated.
          </p>

          <button
            type="button"
            class="btn btn--dark btn--full"
            [disabled]="!canSave"
            (click)="saveChanges()"
          >
            {{ saving ? "Saving Changes..." : "Save Changes" }}
          </button>

          <button
            type="button"
            class="btn btn--ghost btn--full"
            [disabled]="saving"
            (click)="cancel()"
          >
            Keep Original Booking
          </button>
        </section>
      </aside>
    </div>
  }
</div>
